import { useEffect, useState, type FC } from 'react';
import copyIcon from '@assets/images/parrot/copy.svg';
import recentIcon from '@assets/images/header/recent.svg';
import explorerIcon from '@assets/images/header/explorer.svg';
import disconnectIcon from '@assets/images/header/disconnect.svg';
import downIcon from '@assets/images/header/down.svg';
import { useCopyToClipboard } from 'react-use';
import {
  useAccounts,
  useCurrentAccount,
  useCurrentWallet,
  useDisconnectWallet,
  useSwitchAccount,
} from '@mysten/dapp-kit';
import { globalSettingModel } from '@models/global-setting.model';
import { useNavigate } from 'react-router-dom';
import styles from './wallet-list.module.scss';
import { toast } from 'react-toastify';
import { formatAddress } from '@mysten/sui/utils';
import Edit from './edit';
import { OpenapiClientParrot } from 'src/openapi/parrot';
import { WalletAccount } from '@mysten/wallet-standard';
import { Collapse, type CollapseProps } from 'antd';
import { parrotApi } from '@libs/openapi';

interface OwnProps {
  toggleShowMenu(value?: boolean): void;
  changeUser(user: OpenapiClientParrot.GetUsersByWalletResponse): void;
}

const WalletList: FC<OwnProps> = ({ toggleShowMenu, changeUser }) => {
  const currentAccount = useCurrentAccount();
  const accounts = useAccounts();
  const { mutate: switchAccount } = useSwitchAccount();

  const items: CollapseProps['items'] = accounts.map((account) => {
    const isActive = currentAccount?.address === account.address;

    return {
      key: `${account.address}`,
      label: (
        <WalletHeader
          currentAccount={account}
          toggleShowMenu={toggleShowMenu}
          changeUser={changeUser}
          selected={isActive}
        />
      ),
      children: <WalletContent account={account} />,
      showArrow: false,
    };
  });

  return (
    <>
      <div className={styles.bg_wrapper}>
        <div className={styles.switch_account_wrapper}>
          <Collapse
            accordion
            items={items}
            onChange={(value) => {
              const account = accounts.find((item) => item.address === value[0]);
              if (account && account.address !== currentAccount?.address) {
                switchAccount({ account });
              }
            }}
            activeKey={currentAccount?.address}
          />
        </div>
      </div>
    </>
  );
};

const WalletHeader: FC<{ currentAccount: WalletAccount; selected: boolean } & OwnProps> = ({
  toggleShowMenu,
  currentAccount,
  changeUser,
  selected,
}) => {
  const [, copy] = useCopyToClipboard();
  const address = currentAccount.address;
  const wallet = useCurrentWallet();
  const [user, setUser] = useState<OpenapiClientParrot.GetUsersByWalletResponse>();
  const name = user?.username || 'Parrot-' + currentAccount?.address.slice(-4);

  useEffect(() => {
    if (address) {
      try {
        parrotApi
          .get('/users/{wallet}', {
            params: {
              wallet: address,
            },
          })
          .then((user) => {
            setUser(user);
            if (selected) {
              changeUser(user);
            }
          });
      } catch (err) {
        console.log(err);
      }
    }
  }, [address]);

  return (
    <div className={styles.account_row}>
      <div>
        <img src={user?.avatar || wallet.currentWallet?.icon} className={styles.wallet_icon} />
      </div>
      <div className={styles.center}>
        <div className={styles.name}>{name}</div>
        <div className={styles.address}>
          <div className={styles.address_name}>{formatAddress(currentAccount?.address || '')}</div>
          <div
            className={styles.copy}
            onClick={(e) => {
              e.stopPropagation();
              copy(address);
              toast('Address is copied');
            }}
          >
            <img src={copyIcon} data-icon />
          </div>
        </div>
      </div>
      {selected && <Edit toggleShowMenu={toggleShowMenu} user={user} />}
      <img
        src={downIcon}
        data-icon
        className={`${styles.down_icon} ${selected ? styles.up : ''}`}
      />
    </div>
  );
};

const WalletContent: FC<{ account: WalletAccount }> = ({ account }) => {
  const navigate = useNavigate();
  const { mutate: disconnect } = useDisconnectWallet();

  const link = (url: string, blank: boolean = false) => {
    if (blank) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };
  return (
    <ul>
      <li onClick={() => link('/account/transaction')}>
        <img src={recentIcon} data-icon />
        <span>Recent</span>
      </li>

      <li onClick={() => link(globalSettingModel.explorerUrlForAccount(account.address), true)}>
        <img src={explorerIcon} data-icon />
        <span>Explorer</span>
      </li>

      <li onClick={() => disconnect()}>
        <img src={disconnectIcon} data-icon />
        <span className={styles.red}>Disconnect</span>
      </li>
    </ul>
  );
};

export default WalletList;
